import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import apiClient from "../utils/apiClient";
import { RefreshCw, LogOut, ChevronDown, ChevronRight, ShieldAlert, Plus, Users, Target, Trash2, X } from "lucide-react";
import toast from "react-hot-toast";
import { useLtpStore } from "../contexts/LTPStore/useLTPStore";
import RmsArbOrderModal from "./RmsArbOrderModal";
import RmsAddLegModal from "./RmsAddLegModal";

function formatMoney(num) {
    if (num === undefined || num === null || isNaN(num)) return "-";
    const val = Number(num);
    const formatted = Math.abs(val).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return val < 0 ? `-₹${formatted}` : `+₹${formatted}`;
}

export function RmsArbPositionsView({ users = [] }) {
    const [baskets, setBaskets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedBaskets, setExpandedBaskets] = useState(new Set());
    const [orderModalOpen, setOrderModalOpen] = useState(false);

    const isInitialLoadRef = useRef(true);
    const knownBasketIdsRef = useRef(new Set());

    // Edit Target PnL Modal State
    const [targetModal, setTargetModal] = useState({
        show: false,
        basketId: "",
        targetPnl: "",
        submitting: false,
    });

    // Edit Stop Loss PnL Modal State
    const [slModal, setSlModal] = useState({
        show: false,
        basketId: "",
        stopLossPnl: "",
        submitting: false,
    });

    // Add Leg Modal State
    const [addLegModal, setAddLegModal] = useState({
        show: false,
        basketId: "",
        participantClientIds: [],
    });

    // Custom Exit Confirmation Window Modal State
    const [confirmExitModal, setConfirmExitModal] = useState({
        show: false,
        type: "basket", // "basket" or "client"
        basketId: "",
        clientId: "",
        submitting: false,
    });

    const ltps = useLtpStore((state) => state.ltps);

    const fetchActiveBaskets = useCallback(async () => {
        try {
            const res = await apiClient.get("rms-arb/active-baskets");
            if (res.data && res.data.status === "success") {
                const data = res.data.data || [];
                setBaskets(data);

                // Expand active baskets only on initial load
                if (isInitialLoadRef.current && data.length > 0) {
                    const initialIds = new Set(data.map(b => b.basket_id));
                    setExpandedBaskets(initialIds);
                    knownBasketIdsRef.current = new Set(initialIds);
                    isInitialLoadRef.current = false;
                } else if (!isInitialLoadRef.current && data.length > 0) {
                    // Auto-expand only newly created baskets without altering user-collapsed states
                    data.forEach(b => {
                        if (!knownBasketIdsRef.current.has(b.basket_id)) {
                            knownBasketIdsRef.current.add(b.basket_id);
                            setExpandedBaskets(prev => new Set(prev).add(b.basket_id));
                        }
                    });
                }
            }
        } catch (err) {
            console.error("Error fetching active baskets:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleCloseOrderModal = useCallback(() => {
        setOrderModalOpen(false);
    }, []);

    const handleCloseAddLegModal = useCallback(() => {
        setAddLegModal({ show: false, basketId: "", participantClientIds: [] });
    }, []);

    useEffect(() => {
        fetchActiveBaskets();
        const interval = setInterval(fetchActiveBaskets, 3000);
        return () => clearInterval(interval);
    }, [fetchActiveBaskets]);

    const toggleExpand = (basketId) => {
        setExpandedBaskets((prev) => {
            const next = new Set(prev);
            if (next.has(basketId)) next.delete(basketId);
            else next.add(basketId);
            return next;
        });
    };

    // Strict live lookup for NSE:USDINR rate (excluding any CDS)
    const liveUsdinrRate = useMemo(() => {
        for (const [key, val] of Object.entries(ltps || {})) {
            const upperKey = key.toUpperCase();
            if (upperKey.startsWith("NSE:USDINR") || upperKey === "USDINR" || (upperKey.includes("USDINR") && !upperKey.startsWith("CDS:"))) {
                const parsed = Number(val);
                if (parsed > 0) return parsed;
            }
        }
        return null;
    }, [ltps]);

    // Calculate live leg MTM and basket combined MTM dynamically, grouping legs by client within each basket
    const processedBaskets = useMemo(() => {
        return baskets
            .map((b) => {
                let basketCombinedMtm = 0;
                // Only process legs that are OPEN (ignore CLOSED_MANUAL / CLOSED)
                const openLegs = (b.legs || []).filter(
                    (leg) => !leg.status || leg.status === "OPEN"
                );

                const processedLegs = openLegs.map((leg) => {
                    const sym = (leg.symbol || "").toUpperCase();
                    const ex = (leg.exchange || "").toUpperCase();
                    const ltpVal = ltps[sym] || leg.current_ltp || leg.entry_price || 0;
                    const currentLtp = Number(ltpVal);
                    const entryPrice = Number(leg.entry_price || leg.limit_price || 0);
                    const side = Number(leg.side || 1);
                    const qty = Number(leg.qty || 1);
                    const lotSize = Number(leg.lot_size || 1);

                    const isUsdCommodity = ex.includes("NYMEX") || ex.includes("COMEX") || ex.includes("GLBX") || ex.includes("XCEC") || sym.includes("NYMEX") || sym.includes("COMEX");

                    let legMtm = null;
                    let isPendingUsdinr = false;

                    if (isUsdCommodity) {
                        if (liveUsdinrRate && liveUsdinrRate > 0) {
                            legMtm = (currentLtp - entryPrice) * side * (qty * lotSize) * liveUsdinrRate;
                        } else {
                            legMtm = null;
                            isPendingUsdinr = true;
                        }
                    } else {
                        legMtm = (currentLtp - entryPrice) * side * (qty * lotSize);
                    }

                    if (legMtm !== null) {
                        basketCombinedMtm += legMtm;
                    }

                    return {
                        ...leg,
                        currentLtp,
                        legMtm,
                        isUsdCommodity,
                        isPendingUsdinr,
                        usdinrRate: liveUsdinrRate,
                    };
                });

                // Group open legs inside this basket by client_id
                const clientMap = new Map();
                processedLegs.forEach((leg) => {
                    const cid = leg.client_id || "VIRTUAL";
                    if (!clientMap.has(cid)) {
                        const matchedUser = users.find(u => (u.client_id || u.clientId) === cid);
                        clientMap.set(cid, {
                            clientId: cid,
                            clientName: matchedUser?.name || null,
                            legs: [],
                            clientMtm: 0,
                            realLegs: 0,
                            virtualLegs: 0,
                        });
                    }
                    const group = clientMap.get(cid);
                    group.legs.push(leg);
                    if (leg.legMtm !== null) {
                        group.clientMtm += leg.legMtm;
                    }
                    if (leg.is_virtual) group.virtualLegs++;
                    else group.realLegs++;
                });

                const clientSubGroups = Array.from(clientMap.values());
                const participantClientIds = clientSubGroups.map(g => g.clientId);

                return {
                    ...b,
                    liveCombinedMtm: basketCombinedMtm,
                    legs: processedLegs,
                    clientSubGroups,
                    participantClientIds,
                };
            })
            .filter((b) => b.legs && b.legs.length > 0);
    }, [baskets, ltps, liveUsdinrRate, users]);

    const totalStats = useMemo(() => {
        let totalMtm = 0;
        let realLegs = 0;
        let virtualLegs = 0;
        const allClientsSet = new Set();

        processedBaskets.forEach((b) => {
            totalMtm += b.liveCombinedMtm;
            (b.participantClientIds || []).forEach(cid => allClientsSet.add(cid));
            (b.legs || []).forEach((l) => {
                if (l.is_virtual) virtualLegs++;
                else realLegs++;
            });
        });

        return {
            totalClients: allClientsSet.size,
            totalBaskets: processedBaskets.length,
            totalMtm,
            realLegs,
            virtualLegs,
        };
    }, [processedBaskets]);

    // Triggers custom exit confirmation window modal
    const openExitBasketModal = (basketId) => {
        setConfirmExitModal({
            show: true,
            type: "basket",
            basketId,
            clientId: "",
            submitting: false,
        });
    };

    const openExitClientModal = (basketId, clientId) => {
        setConfirmExitModal({
            show: true,
            type: "client",
            basketId,
            clientId,
            submitting: false,
        });
    };

    const executeExit = async () => {
        setConfirmExitModal(prev => ({ ...prev, submitting: true }));
        try {
            if (confirmExitModal.type === "basket") {
                const res = await apiClient.post("rms-arb/exit-basket", { basket_id: confirmExitModal.basketId });
                if (res.data && res.data.status === 200) {
                    toast.success(`Basket ${confirmExitModal.basketId} exit initiated.`);
                    fetchActiveBaskets();
                } else {
                    toast.error(res.data?.message || "Failed to exit basket.");
                }
            } else if (confirmExitModal.type === "client") {
                const res = await apiClient.post("rms-arb/exit-client-in-basket", {
                    basket_id: confirmExitModal.basketId,
                    client_id: confirmExitModal.clientId,
                });
                if (res.data && res.data.status === 200) {
                    toast.success(res.data.message || `Client ${confirmExitModal.clientId} legs in Basket ${confirmExitModal.basketId} exited.`);
                    fetchActiveBaskets();
                } else {
                    toast.error(res.data?.message || "Failed to exit client legs in basket.");
                }
            }
        } catch (err) {
            console.error("Error executing exit:", err);
            toast.error("Failed to execute exit operation.");
        } finally {
            setConfirmExitModal({ show: false, type: "basket", basketId: "", clientId: "", submitting: false });
        }
    };

    const handleSaveTarget = async (e) => {
        e.preventDefault();
        setTargetModal((prev) => ({ ...prev, submitting: true }));
        try {
            const val = targetModal.targetPnl !== "" ? Number(targetModal.targetPnl) : null;
            const res = await apiClient.post("rms-arb/modify-rms", {
                basket_id: targetModal.basketId,
                target_pnl: val,
                clear_target: val === null,
            });
            if (res.data && res.data.status === 200) {
                toast.success(`Target PnL ${val !== null ? `set to +₹${val}` : "cleared"} for basket ${targetModal.basketId}!`);
                setTargetModal({ show: false, basketId: "", targetPnl: "", submitting: false });
                fetchActiveBaskets();
            } else {
                toast.error(res.data?.message || "Failed to update Target PnL.");
            }
        } catch (err) {
            console.error("Error updating Target PnL:", err);
            toast.error("Error updating Target PnL.");
        } finally {
            setTargetModal((prev) => ({ ...prev, submitting: false }));
        }
    };

    const handleClearTarget = async () => {
        setTargetModal((prev) => ({ ...prev, submitting: true }));
        try {
            const res = await apiClient.post("rms-arb/modify-rms", {
                basket_id: targetModal.basketId,
                clear_target: true,
            });
            if (res.data && res.data.status === 200) {
                toast.success(`Target PnL rule cleared for basket ${targetModal.basketId}!`);
                setTargetModal({ show: false, basketId: "", targetPnl: "", submitting: false });
                fetchActiveBaskets();
            } else {
                toast.error(res.data?.message || "Failed to clear Target PnL.");
            }
        } catch (err) {
            console.error("Error clearing Target PnL:", err);
            toast.error("Error clearing Target PnL.");
        } finally {
            setTargetModal((prev) => ({ ...prev, submitting: false }));
        }
    };

    const handleSaveSl = async (e) => {
        e.preventDefault();
        setSlModal((prev) => ({ ...prev, submitting: true }));
        try {
            const val = slModal.stopLossPnl !== "" ? Number(slModal.stopLossPnl) : null;
            const res = await apiClient.post("rms-arb/modify-rms", {
                basket_id: slModal.basketId,
                stop_loss_pnl: val,
                clear_stop_loss: val === null,
            });
            if (res.data && res.data.status === 200) {
                toast.success(`Stop Loss PnL ${val !== null ? `set to ₹${val}` : "cleared"} for basket ${slModal.basketId}!`);
                setSlModal({ show: false, basketId: "", stopLossPnl: "", submitting: false });
                fetchActiveBaskets();
            } else {
                toast.error(res.data?.message || "Failed to update Stop Loss PnL.");
            }
        } catch (err) {
            console.error("Error updating Stop Loss PnL:", err);
            toast.error("Error updating Stop Loss PnL.");
        } finally {
            setSlModal((prev) => ({ ...prev, submitting: false }));
        }
    };

    const handleClearSl = async () => {
        setSlModal((prev) => ({ ...prev, submitting: true }));
        try {
            const res = await apiClient.post("rms-arb/modify-rms", {
                basket_id: slModal.basketId,
                clear_stop_loss: true,
            });
            if (res.data && res.data.status === 200) {
                toast.success(`Stop Loss PnL rule cleared for basket ${slModal.basketId}!`);
                setSlModal({ show: false, basketId: "", stopLossPnl: "", submitting: false });
                fetchActiveBaskets();
            } else {
                toast.error(res.data?.message || "Failed to clear Stop Loss PnL.");
            }
        } catch (err) {
            console.error("Error clearing Stop Loss PnL:", err);
            toast.error("Error clearing Stop Loss PnL.");
        } finally {
            setSlModal((prev) => ({ ...prev, submitting: false }));
        }
    };

    return (
        <div className="p-4 md:p-6 space-y-6 bg-[#0f1116] min-h-screen text-gray-200">
            {/* Top Bar / Stats Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-[#1a1d29] border border-gray-800 rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-6 flex-wrap">
                    <div>
                        <span className="text-xs text-gray-400 uppercase font-semibold">Active Clients</span>
                        <div className="text-2xl font-extrabold text-white mt-0.5 tabular-nums">
                            {totalStats.totalClients}
                        </div>
                    </div>
                    <div className="h-8 w-px bg-gray-700/60" />
                    <div>
                        <span className="text-xs text-gray-400 uppercase font-semibold">Active Baskets</span>
                        <div className="text-2xl font-extrabold text-white mt-0.5 tabular-nums">
                            {totalStats.totalBaskets}
                        </div>
                    </div>
                    <div className="h-8 w-px bg-gray-700/60" />
                    <div>
                        <span className="text-xs text-gray-400 uppercase font-semibold">Total Combined Live MTM</span>
                        <div className={`text-2xl font-extrabold mt-0.5 tabular-nums ${
                            totalStats.totalMtm > 0 ? "text-emerald-400" : totalStats.totalMtm < 0 ? "text-red-400" : "text-gray-300"
                        }`}>
                            {formatMoney(totalStats.totalMtm)}
                        </div>
                    </div>
                    <div className="h-8 w-px bg-gray-700/60 hidden sm:block" />
                    <div className="hidden sm:block">
                        <span className="text-xs text-gray-400 uppercase font-semibold">Legs Breakdown</span>
                        <div className="text-xs font-semibold text-gray-300 mt-1 flex items-center gap-2">
                            <span className="text-emerald-400">{totalStats.realLegs} Real</span>
                            <span>•</span>
                            <span className="text-amber-400">{totalStats.virtualLegs} Virtual</span>
                        </div>
                    </div>
                    {liveUsdinrRate && (
                        <>
                            <div className="h-8 w-px bg-gray-700/60 hidden md:block" />
                            <div className="hidden md:block">
                                <span className="text-xs text-gray-400 uppercase font-semibold">NSE USDINR</span>
                                <div className="text-xl font-extrabold text-blue-400 mt-0.5 tabular-nums">
                                    ₹{Number(liveUsdinrRate).toFixed(2)}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto justify-end flex-wrap">
                    <button
                        onClick={fetchActiveBaskets}
                        className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 rounded-lg text-xs font-semibold transition-all"
                        title="Refresh Baskets"
                    >
                        <RefreshCw className="w-3.5 h-3.5" />
                        <span>Refresh</span>
                    </button>
                    <button
                        onClick={() => setOrderModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-lg text-xs font-bold shadow-md transition-all"
                    >
                        <Plus className="w-4 h-4" />
                        <span>Place Basket Order</span>
                    </button>
                </div>
            </div>

            {/* Baskets List: Basket Cards with Client Sub-Sections */}
            {loading && processedBaskets.length === 0 ? (
                <div className="flex items-center justify-center p-12 bg-[#1a1d29] rounded-xl border border-gray-800">
                    <RefreshCw className="w-6 h-6 text-blue-400 animate-spin mr-3" />
                    <span className="text-gray-400 text-sm font-medium">Loading Active RMS Baskets...</span>
                </div>
            ) : processedBaskets.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 bg-[#1a1d29] rounded-xl border border-gray-800 text-center">
                    <ShieldAlert className="w-10 h-10 text-gray-600 mb-3" />
                    <h3 className="text-white text-base font-bold">No Active RMS Arbitrage Baskets</h3>
                    <p className="text-gray-400 text-xs mt-1">Click "Place Basket Order" above to execute a multi-leg arbitrage basket across your clients.</p>
                </div>
            ) : (
                <div className="space-y-5">
                    {processedBaskets.map((basket) => {
                        const isExpanded = expandedBaskets.has(basket.basket_id);
                        const isProfitable = basket.liveCombinedMtm >= 0;

                        return (
                            <div
                                key={basket.basket_id}
                                className="bg-[#1a1d29] border border-gray-800 rounded-xl overflow-hidden shadow-lg transition-all"
                            >
                                {/* Basket Card Header */}
                                <div
                                    onClick={() => toggleExpand(basket.basket_id)}
                                    className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-[#141722] hover:bg-[#181b28] cursor-pointer border-b border-gray-800/80 gap-3"
                                >
                                    <div className="flex items-center gap-3">
                                        <button className="text-gray-400 hover:text-white p-1">
                                            {isExpanded ? <ChevronDown className="w-4 h-4 text-indigo-400" /> : <ChevronRight className="w-4 h-4 text-indigo-400" />}
                                        </button>
                                        <div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-white font-mono font-bold text-sm tracking-wide">
                                                    {basket.basket_id}
                                                </span>
                                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30 uppercase">
                                                    {(basket.strategy_type || "cross_exchange_arb").replace(/_/g, " ")}
                                                </span>
                                                {/* Participating Clients Badges */}
                                                {(basket.participantClientIds || []).map((cid) => (
                                                    <span key={cid} className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 font-mono">
                                                        Client: {cid}
                                                    </span>
                                                ))}
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                                                    basket.status === "ACTIVE"
                                                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                                                        : "bg-gray-800 text-gray-400 border-gray-700"
                                                }`}>
                                                    {basket.status}
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-400 mt-1 flex items-center gap-3">
                                                <span>Total Legs: <strong className="text-white font-mono">{basket.legs?.length || 0}</strong></span>
                                                <span>•</span>
                                                <span>Clients: <strong className="text-indigo-300 font-mono">{basket.participantClientIds?.length || 0}</strong></span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* MTM & RMS Rules summary */}
                                    <div className="flex items-center gap-6 justify-between md:justify-end">
                                        <div className="text-right">
                                            <span className="text-[11px] text-gray-400 uppercase font-semibold block">Basket Combined MTM</span>
                                            <span className={`text-base font-extrabold tabular-nums ${
                                                isProfitable ? "text-emerald-400" : "text-red-400"
                                            }`}>
                                                {formatMoney(basket.liveCombinedMtm)}
                                            </span>
                                        </div>

                                        <div className="text-right hidden sm:block">
                                            <span className="text-[11px] text-gray-400 uppercase font-semibold block">Client Target / SL</span>
                                            <div className="text-xs font-semibold tabular-nums">
                                                <span className="text-emerald-400">{basket.target_pnl ? `+₹${basket.target_pnl}` : "None"}</span>
                                                <span className="text-gray-500 mx-1">/</span>
                                                <span className="text-red-400">{basket.stop_loss_pnl ? `₹${basket.stop_loss_pnl}` : "None"}</span>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={() => setAddLegModal({
                                                    show: true,
                                                    basketId: basket.basket_id,
                                                    participantClientIds: basket.participantClientIds || [],
                                                })}
                                                className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400 border border-indigo-500/30 rounded-lg text-xs font-semibold transition-all"
                                                title="Add New Leg to Basket"
                                            >
                                                <Plus className="w-3.5 h-3.5" />
                                                <span className="hidden lg:inline">Add Leg</span>
                                            </button>
                                            <button
                                                onClick={() => setTargetModal({
                                                    show: true,
                                                    basketId: basket.basket_id,
                                                    targetPnl: basket.target_pnl ?? "",
                                                    submitting: false,
                                                })}
                                                className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/30 rounded-lg text-xs font-semibold transition-all"
                                                title="Set Target PnL Rule for Basket Clients"
                                            >
                                                <Target className="w-3.5 h-3.5 text-emerald-400" />
                                                <span>Target</span>
                                            </button>
                                            <button
                                                onClick={() => setSlModal({
                                                    show: true,
                                                    basketId: basket.basket_id,
                                                    stopLossPnl: basket.stop_loss_pnl ?? "",
                                                    submitting: false,
                                                })}
                                                className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-500/15 hover:bg-rose-500/25 text-rose-400 border border-rose-500/30 rounded-lg text-xs font-semibold transition-all"
                                                title="Set Stop Loss PnL Rule for Basket Clients"
                                            >
                                                <ShieldAlert className="w-3.5 h-3.5 text-rose-400" />
                                                <span>SL</span>
                                            </button>
                                            <button
                                                onClick={() => openExitBasketModal(basket.basket_id)}
                                                className="flex items-center gap-1 px-2.5 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg text-xs font-semibold transition-all"
                                                title="Unwind / Exit Entire Basket across all clients"
                                            >
                                                <LogOut className="w-3.5 h-3.5" />
                                                <span>Exit Basket</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded Basket Content: Client Sub-Sections */}
                                {isExpanded && (
                                    <div className="p-4 space-y-4 bg-[#11131c]">
                                        {(basket.clientSubGroups || []).map((clientGroup) => {
                                            const isClientProfitable = clientGroup.clientMtm >= 0;

                                            return (
                                                <div
                                                    key={clientGroup.clientId}
                                                    className="bg-[#171a26] border border-gray-800/80 rounded-xl overflow-hidden shadow-inner"
                                                >
                                                    {/* Client Sub-Header inside Basket */}
                                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-[#1c2030] border-b border-gray-800/60 gap-3">
                                                        <div className="flex items-center gap-2.5 flex-wrap">
                                                            <div className="p-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-400">
                                                                <Users className="w-4 h-4" />
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-white font-bold text-xs tracking-wide">
                                                                    {clientGroup.clientName ? `${clientGroup.clientName}` : `Client: ${clientGroup.clientId}`}
                                                                </span>
                                                                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                                                                    {clientGroup.clientId}
                                                                </span>
                                                            </div>
                                                            <div className="text-[11px] text-gray-400 ml-1">
                                                                <span className="text-emerald-400 font-semibold">{clientGroup.realLegs} Real</span>
                                                                {clientGroup.virtualLegs > 0 && (
                                                                    <>
                                                                        <span className="mx-1">•</span>
                                                                        <span className="text-amber-400 font-semibold">{clientGroup.virtualLegs} Virtual</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Client RMS Rule Display, Client MTM & Action Buttons */}
                                                        <div className="flex items-center gap-3 justify-between sm:justify-end flex-wrap">
                                                            <div className="text-right mr-1">
                                                                <span className="text-[10px] text-gray-400 uppercase font-semibold block">Client RMS (Target / SL)</span>
                                                                <div className="text-xs font-semibold tabular-nums">
                                                                    <span className="text-emerald-400">{basket.target_pnl ? `+₹${basket.target_pnl}` : "None"}</span>
                                                                    <span className="text-gray-500 mx-1">/</span>
                                                                    <span className="text-red-400">{basket.stop_loss_pnl ? `₹${basket.stop_loss_pnl}` : "None"}</span>
                                                                </div>
                                                            </div>

                                                            <div className="text-right mr-2">
                                                                <span className="text-[10px] text-gray-400 uppercase font-semibold block">Client MTM</span>
                                                                <span className={`text-xs font-bold tabular-nums ${
                                                                    isClientProfitable ? "text-emerald-400" : "text-red-400"
                                                                }`}>
                                                                    {formatMoney(clientGroup.clientMtm)}
                                                                </span>
                                                            </div>

                                                            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                                <button
                                                                    onClick={() => setTargetModal({
                                                                        show: true,
                                                                        basketId: basket.basket_id,
                                                                        targetPnl: basket.target_pnl ?? "",
                                                                        submitting: false,
                                                                    })}
                                                                    className="flex items-center gap-1 px-2 py-1 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/30 rounded-lg text-[11px] font-semibold transition-all shadow-sm"
                                                                    title={`Set Target PnL for Client ${clientGroup.clientId} in Basket ${basket.basket_id}`}
                                                                >
                                                                    <Target className="w-3 h-3 text-emerald-400" />
                                                                    <span>Target</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => setSlModal({
                                                                        show: true,
                                                                        basketId: basket.basket_id,
                                                                        stopLossPnl: basket.stop_loss_pnl ?? "",
                                                                        submitting: false,
                                                                    })}
                                                                    className="flex items-center gap-1 px-2 py-1 bg-rose-500/15 hover:bg-rose-500/25 text-rose-400 border border-rose-500/30 rounded-lg text-[11px] font-semibold transition-all shadow-sm"
                                                                    title={`Set Stop Loss PnL for Client ${clientGroup.clientId} in Basket ${basket.basket_id}`}
                                                                >
                                                                    <ShieldAlert className="w-3 h-3 text-rose-400" />
                                                                    <span>SL</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => openExitClientModal(basket.basket_id, clientGroup.clientId)}
                                                                    className="flex items-center gap-1 px-2.5 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg text-[11px] font-semibold transition-all shadow-sm"
                                                                    title={`Exit legs for Client ${clientGroup.clientId} in Basket ${basket.basket_id}`}
                                                                >
                                                                    <LogOut className="w-3 h-3" />
                                                                    <span>Exit Client</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Legs Table for this Client */}
                                                    <div className="p-3 overflow-x-auto">
                                                        <table className="w-full text-left text-xs border-collapse">
                                                            <thead>
                                                                <tr className="border-b border-gray-800 text-gray-400 font-semibold uppercase tracking-wider text-[11px]">
                                                                    <th className="py-2 px-3">#</th>
                                                                    <th className="py-2 px-3">Symbol</th>
                                                                    <th className="py-2 px-3">Exchange</th>
                                                                    <th className="py-2 px-3">Side</th>
                                                                    <th className="py-2 px-3">Lots</th>
                                                                    <th className="py-2 px-3">Entry Price</th>
                                                                    <th className="py-2 px-3">Live LTP</th>
                                                                    <th className="py-2 px-3 text-right">Leg MTM</th>
                                                                    <th className="py-2 px-3">Broker</th>
                                                                    <th className="py-2 px-3 text-center">Type</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-800/50 text-gray-200 font-mono">
                                                                {(clientGroup.legs || []).map((leg, lIdx) => {
                                                                    const isBuy = Number(leg.side) === 1;
                                                                    const isLegProfitable = leg.legMtm >= 0;

                                                                    return (
                                                                        <tr key={leg.leg_id || lIdx} className="hover:bg-gray-800/30 transition-colors">
                                                                            <td className="py-2.5 px-3 text-gray-500">{lIdx + 1}</td>
                                                                            <td className="py-2.5 px-3 font-bold text-white">{leg.symbol}</td>
                                                                            <td className="py-2.5 px-3">{leg.exchange || "MCX"}</td>
                                                                            <td className="py-2.5 px-3">
                                                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                                                                    isBuy ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" : "bg-red-500/20 text-red-400 border border-red-500/30"
                                                                                }`}>
                                                                                    {isBuy ? "BUY" : "SELL"}
                                                                                </span>
                                                                            </td>
                                                                            <td className="py-2.5 px-3">{leg.qty}</td>
                                                                            <td className="py-2.5 px-3">{leg.entry_price || leg.limit_price || "-"}</td>
                                                                            <td className="py-2.5 px-3 text-blue-300 font-semibold">{leg.currentLtp || "-"}</td>
                                                                            <td className="py-2.5 px-3 text-right tabular-nums">
                                                                                {leg.isPendingUsdinr ? (
                                                                                    <span className="inline-block text-amber-400 text-[10px] font-semibold bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20 whitespace-nowrap">
                                                                                        Waiting for USDINR
                                                                                    </span>
                                                                                ) : (
                                                                                    <div className="flex flex-col items-end">
                                                                                        <span className={`font-bold ${isLegProfitable ? "text-emerald-400" : "text-red-400"}`}>
                                                                                            {formatMoney(leg.legMtm)}
                                                                                        </span>
                                                                                        {leg.isUsdCommodity && leg.usdinrRate && (
                                                                                            <span className="text-[10px] text-blue-400/80 font-mono">
                                                                                                @ ₹{Number(leg.usdinrRate).toFixed(2)}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                            <td className="py-2.5 px-3 text-gray-400">
                                                                                {leg.broker ? leg.broker.toUpperCase() : leg.client_id}
                                                                            </td>
                                                                            <td className="py-2.5 px-3 text-center">
                                                                                {leg.is_virtual ? (
                                                                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                                                                        VIRTUAL
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                                                                        REAL
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Custom Exit Confirmation Window Modal */}
            {confirmExitModal.show && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-[#1a1d29] border border-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
                        <div className="flex items-center gap-3 border-b border-gray-800 pb-3">
                            <div className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400">
                                <ShieldAlert className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-white text-base font-bold">
                                    {confirmExitModal.type === "basket" ? "Exit Basket Confirmation" : "Exit Client Confirmation"}
                                </h3>
                                <p className="text-xs text-gray-400 font-mono">
                                    Basket ID: {confirmExitModal.basketId}
                                </p>
                            </div>
                        </div>

                        <p className="text-xs text-gray-300 leading-relaxed">
                            {confirmExitModal.type === "basket" ? (
                                <>Are you sure you want to unwind/exit <strong className="text-white">all open legs</strong> in Basket <span className="text-amber-400 font-mono">{confirmExitModal.basketId}</span> across all clients?</>
                            ) : (
                                <>Are you sure you want to unwind/exit all open legs for <strong className="text-indigo-400">Client {confirmExitModal.clientId}</strong> in Basket <span className="text-amber-400 font-mono">{confirmExitModal.basketId}</span>?</>
                            )}
                        </p>

                        <div className="flex items-center justify-end gap-3 pt-3">
                            <button
                                type="button"
                                onClick={() => setConfirmExitModal({ show: false, type: "basket", basketId: "", clientId: "", submitting: false })}
                                className="px-4 py-2 rounded-xl bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-semibold transition"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={confirmExitModal.submitting}
                                onClick={executeExit}
                                className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold shadow-lg shadow-red-600/20 disabled:opacity-50 transition"
                            >
                                <LogOut className="w-3.5 h-3.5" />
                                <span>{confirmExitModal.submitting ? "Exiting..." : "Confirm Exit"}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Target PnL Modal */}
            {targetModal.show && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-[#1a1d29] border border-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
                        <div className="flex items-center justify-between border-b border-gray-800 pb-3">
                            <div className="flex items-center gap-2.5">
                                <div className="p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                                    <Target className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-white text-base font-bold">Set Target PnL</h3>
                                    <p className="text-xs text-gray-400 font-mono">Basket: {targetModal.basketId}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setTargetModal({ show: false, basketId: "", targetPnl: "", submitting: false })}
                                className="p-1 text-gray-400 hover:text-white rounded-lg transition"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSaveTarget} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-emerald-400 mb-1">
                                    Target PnL (₹ per Client)
                                </label>
                                <input
                                    type="number"
                                    step="any"
                                    placeholder="e.g. 5000"
                                    value={targetModal.targetPnl}
                                    onChange={(e) => setTargetModal((prev) => ({ ...prev, targetPnl: e.target.value }))}
                                    className="w-full bg-[#11131c] border border-gray-700 rounded-xl px-3 py-2 text-sm text-white font-mono placeholder-gray-600 focus:outline-none focus:border-emerald-500 transition"
                                />
                                <span className="text-[11px] text-gray-400 mt-1 block">
                                    When any client's MTM crosses above this profit threshold, their position will auto-liquidate.
                                </span>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-gray-800">
                                <button
                                    type="button"
                                    disabled={targetModal.submitting}
                                    onClick={handleClearTarget}
                                    className="flex items-center gap-1 px-3 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-xs font-semibold transition"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    <span>Clear Target Rule</span>
                                </button>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setTargetModal({ show: false, basketId: "", targetPnl: "", submitting: false })}
                                        className="px-4 py-2 rounded-xl bg-gray-800 text-gray-300 hover:bg-gray-700 text-xs font-semibold transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={targetModal.submitting}
                                        className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 disabled:opacity-50 transition"
                                    >
                                        {targetModal.submitting ? "Saving..." : "Save Target"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Stop Loss PnL Modal */}
            {slModal.show && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-[#1a1d29] border border-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
                        <div className="flex items-center justify-between border-b border-gray-800 pb-3">
                            <div className="flex items-center gap-2.5">
                                <div className="p-2 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400">
                                    <ShieldAlert className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-white text-base font-bold">Set Stop Loss PnL</h3>
                                    <p className="text-xs text-gray-400 font-mono">Basket: {slModal.basketId}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSlModal({ show: false, basketId: "", stopLossPnl: "", submitting: false })}
                                className="p-1 text-gray-400 hover:text-white rounded-lg transition"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSaveSl} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-rose-400 mb-1">
                                    Stop Loss PnL (₹ per Client)
                                </label>
                                <input
                                    type="number"
                                    step="any"
                                    placeholder="e.g. -10000"
                                    value={slModal.stopLossPnl}
                                    onChange={(e) => setSlModal((prev) => ({ ...prev, stopLossPnl: e.target.value }))}
                                    className="w-full bg-[#11131c] border border-gray-700 rounded-xl px-3 py-2 text-sm text-white font-mono placeholder-gray-600 focus:outline-none focus:border-rose-500 transition"
                                />
                                <span className="text-[11px] text-gray-400 mt-1 block">
                                    When any client's MTM drops below this loss threshold, their position will auto-liquidate.
                                </span>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-gray-800">
                                <button
                                    type="button"
                                    disabled={slModal.submitting}
                                    onClick={handleClearSl}
                                    className="flex items-center gap-1 px-3 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-xs font-semibold transition"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    <span>Clear SL Rule</span>
                                </button>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setSlModal({ show: false, basketId: "", stopLossPnl: "", submitting: false })}
                                        className="px-4 py-2 rounded-xl bg-gray-800 text-gray-300 hover:bg-gray-700 text-xs font-semibold transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={slModal.submitting}
                                        className="px-5 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold shadow-lg shadow-rose-600/20 disabled:opacity-50 transition"
                                    >
                                        {slModal.submitting ? "Saving..." : "Save Stop Loss"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Place Order Modal */}
            <RmsArbOrderModal
                isOpen={orderModalOpen}
                onClose={handleCloseOrderModal}
                users={users}
                onSuccess={fetchActiveBaskets}
            />

            {/* Add Leg Modal */}
            <RmsAddLegModal
                isOpen={addLegModal.show}
                onClose={handleCloseAddLegModal}
                basketId={addLegModal.basketId}
                participantClientIds={addLegModal.participantClientIds}
                users={users}
                onSuccess={fetchActiveBaskets}
            />
        </div>
    );
}
